/**
 * Indicator value formatting functions
 * Data transformation utilities for display
 *
 * @file Formatting functions for indicator values
 */

/**
 * Indicator value formatting functions
 * @type {Object}
 */
export const indicatorsFormatting = {
  /**
   * Rounds a numeric value to the nearest integer
   *
   * @param {number|null} value - Value to round
   * @returns {number|null} Rounded value or null if input is null
   */
  round: value => (value === null ? null : Math.round(value)),

  /**
   * Multiplies a value by 100 and rounds to the nearest integer
   * Useful for converting decimal percentages to integer percentages
   *
   * @param {number|null} value - Value to multiply and round
   * @returns {number|null} Multiplied and rounded value or null if input is null
   */
  multiply100: value => (value === null ? null : Math.round(value * 100)),

  /**
   * Rounds a numeric value to one decimal place
   *
   * @param {number|null} value - Value to round
   * @returns {number|null} Rounded value with one decimal place or null if input is null
   */
  oneDecimal: value => (value === null ? null : Number((Math.round(value * 10) / 10).toFixed(1))),

  /**
   * Converts a Julian day number (1-365) to DD/MM format
   * Used for displaying dates within a year without the year component
   *
   * @param {number|null} value - Julian day number (1-365)
   * @returns {string|null} Date formatted as DD/MM or null if input is null
   * @example
   * jourJulien(32) // Returns "01/02" (February 1st)
   * jourJulien(365) // Returns "30/12" (December 30th)
   */
  jourJulien: value => {
    if (value === null) return null;
    const monthDurationInDays = [31, 28, 31, 30, 31, 30, 31, 30, 31, 30, 31, 30];
    let dayCount = Math.round(value);
    let monthIndex = 0;

    while (dayCount - monthDurationInDays[monthIndex] > 0) {
      dayCount -= monthDurationInDays[monthIndex];
      monthIndex += 1;
    }

    const day = dayCount.toLocaleString('fr-FR', { minimumIntegerDigits: 2, useGrouping: false });
    const month = (monthIndex + 1).toLocaleString('fr-FR', { minimumIntegerDigits: 2, useGrouping: false });

    return `${day}/${month}`;
  },
};

/**
 * Tooltip title configuration
 * Functions to generate tooltip titles for charts
 * @type {Object}
 */
export const tooltipTitle = {
  standard: (indicatorTitle, seasonLabel) => `${indicatorTitle} - ${seasonLabel}`,
  withoutSeasonOrAnnual: (indicatorTitle, _seasonLabel) => `${indicatorTitle}`,
};
